import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {cn} from '../lib/cn';

interface PopoverWithTooltipProps {
  children: React.ReactNode;
  popoverContent: React.ReactNode;
  tooltipContent: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  popoverContentClassName?: string;
  tooltipVariant?: 'light' | 'dark';
}

function PopoverWithTooltip({
  children,
  popoverContent,
  tooltipContent,
  open,
  onOpenChange,
  popoverContentClassName,
  tooltipVariant = 'light',
}: PopoverWithTooltipProps) {
  return (
    <TooltipPrimitive.Provider delayDuration={0}>
      <TooltipPrimitive.Root open={open ? false : undefined}>
        <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
          <TooltipPrimitive.Trigger asChild>
            <PopoverPrimitive.Trigger asChild>{children}</PopoverPrimitive.Trigger>
          </TooltipPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              align="center"
              sideOffset={4}
              className={cn(
                'z-50 w-72 rounded-lg border border-[var(--chekin-gray-200)] bg-white p-4 shadow-lg outline-none',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
                'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
                'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                popoverContentClassName,
              )}
            >
              {popoverContent}
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content
            sideOffset={5}
            className={cn(
              'z-50 w-fit text-balance rounded-md px-4 py-2 text-sm font-medium',
              'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
              'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
              'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
              {
                'bg-white text-[var(--chekin-color-brand-navy)] shadow-[0_0_10px_0_rgba(143,143,143,0.30)]':
                  tooltipVariant === 'light',
                'bg-[var(--chekin-color-brand-navy)] text-white':
                  tooltipVariant === 'dark',
              },
            )}
          >
            {tooltipContent}
            <TooltipPrimitive.Arrow
              className={cn(
                'z-50 size-3 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]',
                {
                  'fill-white bg-white': tooltipVariant === 'light',
                  'fill-[var(--chekin-color-brand-navy)] bg-[var(--chekin-color-brand-navy)]':
                    tooltipVariant === 'dark',
                },
              )}
            />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}

export {PopoverWithTooltip};
export type {PopoverWithTooltipProps};
